"use client";

import { useState, useCallback, useDeferredValue } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { motion, AnimatePresence, useReducedMotion } from "@/lib/motion";
import { BlogCta, EyebrowBadge, HeroGlow, Input, LoadingSpinner } from "@/components/ui/";
import { Search, X } from "@/components/icons";

import {
  HeroFeaturedCard,
  PostListItem,
  CategoryFilterSidebar,
  CategoryFilterSkeleton,
  BlogPostsLoader,
  Pagination,
  BlogArchiveNewsletterForm,
} from "./parts";
import type { Post } from "./types";

type BlogPageResponse = { posts: Post[]; totalPages: number; totalResults: number; page: number };

async function fetchBlogPosts(
  page: number,
  category: string,
  search: string
): Promise<BlogPageResponse> {
  const params = new URLSearchParams({ page: String(page) });
  if (category && category !== "All") params.set("category", category);
  if (search.trim()) params.set("search", search.trim());
  const res = await fetch(`/api/blog/posts?${params}`);
  if (!res.ok) throw new Error("Failed to fetch posts");
  return res.json();
}

export default function BlogArchiveSection({
  initialData,
  initialCategories,
  featuredPost,
}: {
  initialData?: BlogPageResponse;
  initialCategories?: string[];
  featuredPost?: Post;
}) {
  const prefersReducedMotion = useReducedMotion();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(() => searchParams.get("category") ?? "All");
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("search") ?? "");
  const activeSearch = searchQuery.length >= 3 || searchQuery.length === 0 ? searchQuery : "";
  const deferredSearch = useDeferredValue(activeSearch);
  const isDefaultView = activeCategory === "All" && !deferredSearch;
  const pageFromQuery = searchParams.get("page");
  const currentPage = pageFromQuery ? Math.max(1, Number(pageFromQuery)) : (initialData?.page ?? 1);

  // Filtered/search browsing always lives at /blog?... — path-based pagination
  // (/blog/page/N) is reserved for the crawlable, unfiltered default view.
  const setPage = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams.toString());
      if (page <= 1) params.delete("page");
      else params.set("page", String(page));
      const qs = params.toString();
      router.push(`/blog${qs ? `?${qs}` : ""}`, { scroll: false });
    },
    [router, searchParams]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      const params = new URLSearchParams(searchParams.toString());
      if (value.length >= 3) params.set("search", value);
      else params.delete("search");
      params.delete("page");
      const qs = params.toString();
      router.replace(`/blog${qs ? `?${qs}` : ""}`, { scroll: false });
    },
    [router, searchParams]
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["blog-posts", currentPage, activeCategory, deferredSearch],
    queryFn: () => fetchBlogPosts(currentPage, activeCategory, deferredSearch),
    placeholderData: keepPreviousData,
    ...(initialData &&
    currentPage === initialData.page &&
    activeCategory === "All" &&
    !deferredSearch
      ? { initialData }
      : {}),
  });

  const posts = data?.posts ?? [];
  const totalPages = data?.totalPages ?? 1;
  const heroPost = featuredPost ?? initialData?.posts[0] ?? posts[0];

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["blog-categories"],
    queryFn: () =>
      fetch("/api/blog/categories").then((r) => r.json() as Promise<{ categories: string[] }>),
    staleTime: 5 * 60 * 1000,
    ...(initialCategories ? { initialData: { categories: initialCategories } } : {}),
  });
  const allCategories = categoriesData?.categories ?? initialCategories ?? ["All"];
  const showCategorySkeleton = categoriesLoading && allCategories.length <= 1;

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    const params = new URLSearchParams(searchParams.toString());
    if (cat === "All") params.delete("category");
    else params.set("category", cat);
    params.delete("page");
    const qs = params.toString();
    router.push(`/blog${qs ? `?${qs}` : ""}`, { scroll: false });
    const el = document.getElementById("blog-grid");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 160;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handlePageChange = (page: number) => {
    setPage(page);
    const el = document.getElementById("blog-grid");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 160;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  if (!heroPost) return null;

  return (
    <main id="main-content" tabIndex={-1} className="relative pt-24 pb-16 sm:pt-32 sm:pb-32">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <HeroGlow align="left" />
      </div>

      <section className="relative mx-auto mb-10 max-w-[80rem] px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8 flex items-end justify-between gap-6"
        >
          <div>
            <EyebrowBadge>Proxidize Blog</EyebrowBadge>
            <h1 className="text-foreground mb-2 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Blog
            </h1>
            <p className="text-muted-foreground light:text-foreground/80 max-w-xs text-sm leading-relaxed">
              The latest guides, research, and updates from the Proxidize team.
            </p>
          </div>
        </motion.div>

        <HeroFeaturedCard post={heroPost} />

        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="border-border -mx-4 mt-8 flex flex-col justify-between gap-5 border-t border-b px-4 py-7 sm:-mx-6 sm:flex-row sm:items-center sm:px-6 lg:-mx-8 lg:px-8"
        >
          <div>
            <p className="text-foreground text-sm font-semibold">Stay in the loop</p>
            <p className="text-muted-foreground mt-0.5 text-sm">
              Subscribe for the latest news &amp; updates.
            </p>
          </div>
          <BlogArchiveNewsletterForm />
        </motion.div>
      </section>

      <div className="mx-auto mt-14 max-w-[80rem] px-4 sm:px-6 lg:px-8">
        <div className="mb-6 lg:hidden">
          <div className="relative">
            <Search
              size={14}
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search articles…"
              className="bg-card h-9 w-full rounded-full pr-8 pl-8 text-xs"
              aria-label="Search blog posts"
            />
            {searchQuery && (
              <button
                onClick={() => handleSearchChange("")}
                className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-10 lg:grid-cols-[1fr_300px] lg:gap-14 xl:grid-cols-[1fr_320px]">
          <div className="relative min-w-0 self-start">
            <AnimatePresence>
              {isFetching && (
                <motion.div
                  key="blog-posts-loader"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="pointer-events-none absolute inset-x-0 top-32 z-10 flex justify-center"
                  aria-live="polite"
                  aria-busy="true"
                >
                  <BlogPostsLoader />
                </motion.div>
              )}
            </AnimatePresence>

            {isLoading ? (
              <LoadingSpinner label="Loading posts…" />
            ) : posts.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-24 text-center"
              >
                <p className="text-muted-foreground mb-2 text-lg">No articles found.</p>
                <button
                  onClick={() => {
                    handleSearchChange("");
                    setActiveCategory("All");
                  }}
                  className="text-muted-foreground text-sm font-medium"
                >
                  Clear filters
                </button>
              </motion.div>
            ) : (
              <div
                className={
                  isFetching
                    ? "pointer-events-none opacity-40 transition-opacity duration-200"
                    : "transition-opacity duration-200"
                }
              >
                <div
                  id="blog-grid"
                  className="mb-1 flex items-center justify-between gap-2 sm:gap-4"
                >
                  <h2 className="text-foreground text-xl font-bold">
                    {activeCategory === "All" ? "Latest posts" : activeCategory}
                  </h2>
                  <span className="text-muted-foreground text-xs">
                    {data?.totalResults ? `Page ${currentPage} of ${data.totalResults} posts` : ""}
                  </span>
                </div>
                <div className="bg-border mb-2 h-px" aria-hidden="true" />
                <AnimatePresence mode="popLayout">
                  {posts
                    .filter((post) => post.href !== heroPost?.href)
                    .map((post, i) => (
                      <PostListItem key={post.href} post={post} index={i} />
                    ))}
                </AnimatePresence>
                <Pagination
                  current={currentPage}
                  total={totalPages}
                  onChange={handlePageChange}
                  hrefFor={
                    isDefaultView ? (p) => (p <= 1 ? "/blog" : `/blog/page/${p}`) : undefined
                  }
                />
              </div>
            )}
          </div>

          <aside className="hidden lg:block" aria-label="Filter by category">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="sticky top-24 space-y-4"
            >
              <div className="relative" role="search">
                <Search
                  size={14}
                  className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2"
                  aria-hidden="true"
                />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search articles…"
                  className="bg-card border-border h-10 w-full rounded-xl pr-9 pl-9 text-sm"
                  aria-label="Search blog posts"
                />
                {searchQuery && (
                  <button
                    onClick={() => handleSearchChange("")}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                    aria-label="Clear search"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              {showCategorySkeleton ? (
                <CategoryFilterSkeleton />
              ) : (
                <CategoryFilterSidebar
                  categories={allCategories}
                  activeCategory={activeCategory}
                  onCategoryChange={handleCategoryChange}
                />
              )}
            </motion.div>
          </aside>
        </div>
        <BlogCta />
      </div>
    </main>
  );
}
